'use client'

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../components/Input';
import Button from '../components/Button';
import Spinner from "../components/Spinner";
import { useToaster } from '../context/ToasterContext';
import MessageLogView from '../components/MessageLogView';
import { useSelector } from 'react-redux';

import api from '../api/axios';

// ── Schemas ──────────────────────────────────────────────────────────────────

const idSchema = yup.object().shape({
  searchType: yup.string().required('Please select search type'),
  searchValue: yup.string().required('Please enter a search value'),
});

const dateSchema = yup.object().shape({
  accountId: yup.string().required('Account ID is required'),
  rangeType: yup.string().required(),
  minutes: yup.number().nullable().when('rangeType', {
    is: 'minutes',
    then: (s) => s.required('Minutes required').min(1, 'Must be at least 1'),
  }),
  startDate: yup.string().nullable().when('rangeType', {
    is: 'custom',
    then: (s) => s.required('Start date is required'),
  }),
  endDate: yup.string().nullable(),
});

// ── Constants ─────────────────────────────────────────────────────────────────

const QUICK_MINUTES = [15, 30, 60, 120, 240];
const PAGE_SIZES = [10, 25, 50, 100];
const TABS = ['ID Lookup', 'Date / Time Search'];



// ── Sub-components ────────────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div className="flex border-b border-gray-200 dark:border-gray-800 mb-6">
      {TABS.map((tab, i) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(i)}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            active === i
              ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

function SortIcon({ field, sortBy, sortDir }) {
  if (sortBy !== field) return <span className="ml-1 text-gray-300">↕</span>;
  return <span className="ml-1 text-indigo-500">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function StatusBadge({ status, terminal }) {
  if (!status) return <span className="text-gray-400 text-xs">—</span>;
  const base = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
  if (terminal) return <span className={`${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>{status}</span>;
  return <span className={`${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>{status}</span>;
}

function Pagination({ page, totalPages, totalElements, pageSize, onPageChange, onPageSizeChange }) {
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalElements);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-400">
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm"
        >
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <span>{totalElements === 0 ? 'No results' : `${start}–${end} of ${totalElements}`}</span>

      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(0)} disabled={page === 0}
          className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">«</button>
        <button onClick={() => onPageChange(page - 1)} disabled={page === 0}
          className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">‹</button>
        <span className="px-3">Page {page + 1} of {Math.max(totalPages, 1)}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}
          className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">›</button>
        <button onClick={() => onPageChange(totalPages - 1)} disabled={page >= totalPages - 1}
          className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">»</button>
      </div>
    </div>
  );
}

function ResultsTable({ rows, sortBy, sortDir, onSort, page, pageSize, totalPages, totalElements, onPageChange, onPageSizeChange, onRowClick }) {
  const cols = [
    { key: 'id',              label: 'Message ID',     sortable: false },
    { key: 'date',            label: 'Date',           sortable: true  },
    { key: 'templateId',      label: 'Template',       sortable: false },
    { key: 'vendorAccountId', label: 'Vendor Account', sortable: false },
    { key: 'vendorMessageId', label: 'Vendor Msg ID',  sortable: false },
    { key: 'direction',       label: 'Direction',      sortable: false },
    { key: 'fromNumber',      label: 'From',           sortable: false },
    { key: 'toNumber',        label: 'To',             sortable: false },
    { key: 'body',            label: 'Body',           sortable: false },
    { key: 'status',          label: 'Status',         sortable: false },
  ];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {cols.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && onSort(col.key)}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap
                    ${col.sortable ? 'cursor-pointer hover:text-gray-900 dark:hover:text-gray-100 select-none' : ''}`}
                >
                  {col.label}
                  {col.sortable && <SortIcon field={col.key} sortBy={sortBy} sortDir={sortDir} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-black">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cols.length} className="px-4 py-12 text-center text-gray-400">No records found.</td>
              </tr>
            ) : rows.map((row, i) => (
              
              <tr
                key={row.id ?? i}
                onClick={() => onRowClick(row)}
                className="hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.id ?? '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                  {row.date ? new Date(row.date).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[140px] truncate" title={row.templateId}>{row.templateId ?? '—'}</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.vendorAccountId ?? '—'}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400 max-w-[160px] truncate" title={row.vendorMessageId}>{row.vendorMessageId ?? '—'}</td>
                <td className="px-4 py-3">
                  {row.direction
                    ? <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                        ${row.direction === 'INBOUND'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'}`}>
                        {row.direction}
                      </span>
                    : '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.fromNumber ?? '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.toNumber ?? '—'}</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={row.body}>{row.body ?? '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={row.status} terminal={row.terminal} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Logging() {
  const { toaster } = useToaster();

  const [activeTab, setActiveTab] = useState(0);

  // ID lookup state
  const [idResult, setIdResult] = useState(null);

  // Date search state
  const [tableData, setTableData] = useState(null);   // { content, totalElements, totalPages }
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('updateDTTM');
  const [sortDir, setSortDir] = useState('desc');
  const [lastDateParams, setLastDateParams] = useState(null); // for re-fetching on page/sort change

  const [loading, setLoading] = useState(false);
  const activeAccountId = useSelector(s => s.globalData.activeAccountId);

  
  // ── ID Search Form ──

  const idForm = useForm({
    resolver: yupResolver(idSchema),
    defaultValues: { searchType: 'mvId', searchValue: '' },
  });
  const searchType = idForm.watch('searchType');

  // ── Date Search Form ──

  const dateForm = useForm({
  resolver: yupResolver(dateSchema),
  defaultValues: {
    accountId: activeAccountId ?? '',
    rangeType: 'minutes',
    minutes: 15,
    startDate: '',
    endDate: '',
  },
});

useEffect(() => {
  if (activeAccountId) {
    dateForm.setValue('accountId', activeAccountId);
  }
}, [activeAccountId]);

  const rangeType = dateForm.watch('rangeType');

  // ── Handlers ──

  const handleIdSubmit = async (data) => {
    try {
      setIdResult(null);
      setLoading(true);
      const path = data.searchType === 'mvId'
        ? `mvid/${data.searchValue}`
        : `vendorid/${data.searchValue}`;
      const res = await api.get(`${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/correlation/${path}`);
      setIdResult(res.data);
    } catch (err) {
      toaster.error('No record found or something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const buildDateQueryParams = (data, pg, ps, sb, sd) => {
    const params = new URLSearchParams();
    params.set('page', pg);
    params.set('size', ps);
    params.set('sortBy', sb);
    params.set('sortDir', sd);
    if (data.rangeType === 'minutes') {
      params.set('minutes', data.minutes);
    } else {
      if (data.startDate) params.set('startDate', new Date(data.startDate).toISOString());
      if (data.endDate)   params.set('endDate',   new Date(data.endDate).toISOString());
    }
    return params;
  };

  const fetchDateResults = async (data, pg, ps, sb, sd) => {
    setLoading(true);
    try {
      const params = buildDateQueryParams(data, pg, ps, sb, sd);
      const res = await api.get(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/correlation/account/${activeAccountId}/messages?${params}`
      );
      setTableData(res.data);
    } catch (err) {
      toaster.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSubmit = async (data) => {
    setLastDateParams(data);
    setPage(0);
    await fetchDateResults(data, 0, pageSize, sortBy, sortDir);
  };

  const handlePageChange = async (newPage) => {
    setPage(newPage);
    if (lastDateParams) await fetchDateResults(lastDateParams, newPage, pageSize, sortBy, sortDir);
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setPage(0);
    if (lastDateParams) await fetchDateResults(lastDateParams, 0, newSize, sortBy, sortDir);
  };

  const handleSort = async (field) => {
    const newDir = sortBy === field && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(field);
    setSortDir(newDir);
    setPage(0);
    if (lastDateParams) await fetchDateResults(lastDateParams, 0, pageSize, field, newDir);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    setIdResult(null);
    setTableData(null);
  };

  // ── Render ──

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl sm:tracking-tight">Logging</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Search and inspect message logs</p>

      <div className="mt-8 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <TabBar active={activeTab} onChange={handleTabChange} />

        {/* ── Tab 0: ID Lookup ── */}
        {activeTab === 0 && (
          <form onSubmit={idForm.handleSubmit(handleIdSubmit)} className="space-y-6">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Search by</p>
              <Input id="st_mvId" label="Ubiqio ID" as="radio" value="mvId"
                {...idForm.register('searchType')} />
              <Input id="st_vendor" label="Vendor ID" as="radio" value="vendorMessageId"
                {...idForm.register('searchType')} />
            </div>
            <Input
              label={searchType === 'mvId' ? 'Enter Ubiqio ID' : 'Enter Vendor ID'}
              placeholder={searchType === 'mvId' ? 'Enter Ubiqio ID' : 'Enter Vendor ID'}
              required
              {...idForm.register('searchValue')}
              error={idForm.formState.errors.searchValue?.message}
            />
            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={idForm.formState.isSubmitting || loading}>
                {loading ? 'Searching…' : 'Search'}
              </Button>
            </div>
          </form>
        )}

        {/* ── Tab 1: Date/Time Search ── */}
        {activeTab === 1 && (
          <form onSubmit={dateForm.handleSubmit(handleDateSubmit)} className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date range</p>
              <div className="flex flex-col gap-2">
                <Input id="rt_minutes" label="Last X minutes" as="radio" value="minutes"
                  {...dateForm.register('rangeType')} />
                <Input id="rt_custom"  label="Custom range"   as="radio" value="custom"
                  {...dateForm.register('rangeType')} />
              </div>
            </div>

            {rangeType === 'minutes' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Quick select</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_MINUTES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => dateForm.setValue('minutes', m)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors
                        ${dateForm.watch('minutes') == m
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-indigo-400'}`}
                    >
                      {m >= 60 ? `${m / 60}h` : `${m}m`}
                    </button>
                  ))}
                </div>
                <Input
                  label="Or enter minutes"
                  type="number"
                  min={1}
                  {...dateForm.register('minutes')}
                  error={dateForm.formState.errors.minutes?.message}
                />
              </div>
            )}

            {rangeType === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Start date/time"
                  type="datetime-local"
                  required
                  {...dateForm.register('startDate')}
                  error={dateForm.formState.errors.startDate?.message}
                />
                <Input
                  label="End date/time (optional)"
                  type="datetime-local"
                  {...dateForm.register('endDate')}
                />
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={dateForm.formState.isSubmitting || loading}>
                {loading ? 'Searching…' : 'Search'}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="mt-8 flex justify-center">
          <Spinner />
        </div>
      )}

      {/* ── ID Result ── */}
      {!loading && idResult && (
        <div className="mt-6 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800">
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Full data log</p>
            <button onClick={() => setIdResult(null)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕ Clear</button>
          </div>
          <div className="p-6">
            <MessageLogView data={idResult} />
          </div>
        </div>
      )}

      {/* ── Date Results Table ── */}
      {!loading && tableData && (
        <div className="mt-6 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Results
              <span className="ml-2 text-xs font-normal text-gray-500">
                {tableData.totalElements.toLocaleString()} total records
              </span>
            </p>
            <button onClick={() => setTableData(null)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕ Clear</button>
          </div>
          <ResultsTable
            rows={tableData.content}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            page={page}
            pageSize={pageSize}
            totalPages={tableData.totalPages}
            totalElements={tableData.totalElements}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRowClick={(row) => {
              setActiveTab(0);
              idForm.setValue('searchType', 'mvId');
              idForm.setValue('searchValue', row.id);
              idForm.handleSubmit(handleIdSubmit)();
            }}
          />
        </div>
      )}
    </div>
  );
}